import { useRef, useState } from 'react'
import {Loader2, Upload, X} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { nowISO, maskCNPJ, unmaskCNPJ, isValidCNPJ, maskPhone, unmaskPhone } from '@/utils/masks'
import { ESTADOS_BR, getMunicipiosPorEstado } from '@/utils/estadosCidades'

interface CidadeModalProps {
  cidade?: any
  onClose: () => void
  onSaved: () => void
}

function CidadeModal({ cidade, onClose, onSaved }: CidadeModalProps) {
  const { user: currentUser } = useAppAuth()
  const isEdit = !!cidade
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [nome, setNome] = useState(cidade?.nome || '')
  const [estado, setEstado] = useState(cidade?.estado || '')
  const [cidadeNome, setCidadeNome] = useState(cidade?.cidadeNome || '')
  const [cnpj, setCnpj] = useState(cidade?.cnpj ? maskCNPJ(cidade.cnpj) : '')
  const [rua, setRua] = useState(cidade?.endereco?.rua || '')
  const [numero, setNumero] = useState(cidade?.endereco?.numero || '')
  const [bairro, setBairro] = useState(cidade?.endereco?.bairro || '')
  const [telefone, setTelefone] = useState(cidade?.telefone ? maskPhone(cidade.telefone) : '')
  const [email, setEmail] = useState(cidade?.email || '')
  const [nomeSistema, setNomeSistema] = useState(cidade?.nomeSistema || 'PROTOCOLEAI')
  const [logoUrl, setLogoUrl] = useState(cidade?.logoUrl || '')
  const [erros, setErros] = useState<Record<string, string>>({})

  const municipios = getMunicipiosPorEstado(estado)

  const handleEstadoChange = (uf: string) => {
    setEstado(uf)
    setCidadeNome('')
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results?.[0]
      if (result?.fileUrl) {
        setLogoUrl(result.fileUrl)
        toast.success('Logo enviado com sucesso.')
      }
      else {
        toast.error(result?.uploadError || 'Erro ao enviar o logo.')
      }
    }
    catch {
      toast.error('Erro ao enviar o logo. Tente novamente.')
    }
    finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const validar = () => {
    const novosErros: Record<string, string> = {}
    if (!estado) novosErros.estado = 'Selecione o Estado.'
    if (!cidadeNome.trim()) novosErros.cidadeNome = 'Selecione o município.'
    if (!nome.trim()) novosErros.nome = 'Nome da Prefeitura é obrigatório.'
    if (cnpj && !isValidCNPJ(cnpj)) novosErros.cnpj = 'CNPJ inválido.'
    setErros(novosErros)
    return Object.keys(novosErros).length === 0
  }

  const handleSubmit = async () => {
    if (!validar()) return
    setSaving(true)
    try {
      const payload = {
        nome: nome.trim(),
        estado,
        cidadeNome: cidadeNome.trim(),
        cnpj: unmaskCNPJ(cnpj),
        endereco: {
          rua: rua.trim(),
          numero: numero.trim(),
          bairro: bairro.trim(),
        },
        telefone: unmaskPhone(telefone),
        email: email.trim(),
        nomeSistema: nomeSistema.trim() || 'PROTOCOLEAI',
        logoUrl: logoUrl.trim(),
        updatedAt: nowISO(),
      }

      if (isEdit) {
        await (lumi.entities as any).cidades.update(cidade._id, payload)
        toast.success('Cidade atualizada com sucesso.')
      }
      else {
        await (lumi.entities as any).cidades.create({
          ...payload,
          creator: currentUser?.userId || 'system',
          createdAt: nowISO(),
        })
        toast.success('Cidade criada com sucesso.')
      }
      onSaved()
      onClose()
    }
    catch {
      toast.error('Erro ao guardar cidade. Tente novamente.')
    }
    finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-[560px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7E9EC] sticky top-0 bg-white">
          <h2 className="text-[16px] font-semibold text-[#12141A]">
            {isEdit ? 'Editar Cidade' : 'Nova Cidade'}
          </h2>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#12141A]">
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Estado (UF)</label>
              <select
                value={estado}
                onChange={e => handleEstadoChange(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] bg-white"
              >
                <option value="">Selecione...</option>
                {ESTADOS_BR.map(e => (
                  <option key={e.uf} value={e.uf}>{e.nome} ({e.uf})</option>
                ))}
              </select>
              {erros.estado && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.estado}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Município</label>
              <select
                value={cidadeNome}
                onChange={e => setCidadeNome(e.target.value)}
                disabled={!estado}
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] bg-white disabled:bg-[#F5F6F8] disabled:text-[#9CA3AF]"
              >
                <option value="">{estado ? 'Selecione...' : 'Selecione o Estado primeiro'}</option>
                {municipios.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {erros.cidadeNome && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.cidadeNome}</p>}
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nome da Prefeitura</label>
            <input
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: PREFEITURA MUNICIPAL DE CAICÓ"
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
            {erros.nome && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.nome}</p>}
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">CNPJ</label>
            <input
              value={cnpj}
              onChange={e => setCnpj(maskCNPJ(e.target.value))}
              placeholder="00.000.000/0000-00"
              maxLength={18}
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
            {erros.cnpj && <p className="text-[12px] text-[#B91C1C] mt-1">{erros.cnpj}</p>}
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Endereço</label>
            <div className="grid grid-cols-3 gap-3">
              <input
                value={rua}
                onChange={e => setRua(e.target.value)}
                placeholder="Rua"
                className="col-span-2 px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              />
              <input
                value={numero}
                onChange={e => setNumero(e.target.value)}
                placeholder="Número"
                className="px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              />
            </div>
            <input
              value={bairro}
              onChange={e => setBairro(e.target.value)}
              placeholder="Bairro"
              className="w-full mt-3 px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Telefone</label>
              <input
                value={telefone}
                onChange={e => setTelefone(maskPhone(e.target.value))}
                placeholder="(00) 00000-0000"
                maxLength={15}
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="contato@prefeitura.gov.br"
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nome do Sistema</label>
            <input
              value={nomeSistema}
              onChange={e => setNomeSistema(e.target.value)}
              placeholder="PROTOCOLEAI"
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
            <p className="text-[12px] text-[#9CA3AF] mt-1">Nome exibido nos documentos emitidos por esta cidade.</p>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Logo da Prefeitura</label>
            <div className="flex items-center gap-3">
              {logoUrl && (
                <img src={logoUrl} alt="Logo" className="w-14 h-14 rounded-lg object-cover border border-[#E7E9EC]" />
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8] disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.75} />}
                {uploading ? 'A enviar...' : logoUrl ? 'Alterar logo' : 'Enviar logo (PNG)'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8]">
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white disabled:opacity-60"
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CidadeModal
